import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Persists an in-progress public form to localStorage.
 *
 * These forms are public and sessionless — there is no server-side draft to fall
 * back on, so a refresh or a closed tab would otherwise discard everything the
 * applicant typed.
 *
 * `storageKey` is required and must be unique per form: the loan application and
 * the credit authorization run on the same origin, so a shared key would have
 * one restoring the other's answers.
 *
 * Deliberately NOT persisted:
 *  - `borrowerSSN` and `signature` — sensitive, and cheap to re-enter.
 *  - File uploads — File objects don't survive JSON serialization.
 * Everything else round-trips as-is. Dates are stored by JSON's own encoding and
 * restored as strings, which the Rff date fields accept.
 */
const OMITTED_FROM_DRAFT = ["borrowerSSN", "signature", "uploadMetadata"];

/**
 * `submitCheck` for the step that asks for the SSN. A restored draft resumes
 * past that step without the number, so without this the form submits
 * `borrowerHasSSN: true` and no SSN.
 */
export const ssnSubmitCheck = (values) =>
  values?.borrowerHasSSN === true && !values?.borrowerSSN
    ? "Please re-enter your Social Security Number. It isn't saved with your draft, so it needs to be entered again before you submit."
    : null;

// "Don't ask me about drafts again." Kept separate from the draft itself so
// clearing or rewriting a draft doesn't resurrect the prompt.
const dismissKeyFor = (storageKey) => `${storageKey}:restore-dismissed`;

export const usePublicFormDraft = ({ storageKey, enabled = true } = {}) => {
  const [restored, setRestored] = useState(null);
  const [checked, setChecked] = useState(false);
  const savedAtRef = useRef(null);

  // Look for an existing draft once, on mount.
  useEffect(() => {
    if (!enabled) {
      setChecked(true);
      return;
    }
    try {
      // Someone who chose "Ignore" is never prompted again on this browser —
      // check that before even reading the draft.
      if (window.localStorage.getItem(dismissKeyFor(storageKey)) === "true") {
        setRestored(null);
        setChecked(true);
        return;
      }
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.values && Object.keys(parsed.values).length > 0) {
          setRestored(parsed);
        }
      }
    } catch (e) {
      // A malformed or unreadable draft (private mode, quota, hand-edited JSON)
      // should never block the form — just start clean.
      setRestored(null);
    }
    setChecked(true);
  }, [enabled, storageKey]);

  const save = useCallback(
    (values, step) => {
      if (!enabled) return;
      try {
        const safe = { ...values };
        OMITTED_FROM_DRAFT.forEach((key) => delete safe[key]);
        savedAtRef.current = new Date().toISOString();
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ values: safe, step, savedAt: savedAtRef.current }),
        );
      } catch (e) {
        // Quota exceeded or storage disabled — saving is best-effort.
      }
    },
    [enabled, storageKey],
  );

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (e) {
      // no-op
    }
  }, [storageKey]);

  /**
   * "Ignore" — stop offering to restore, on this visit and every future one,
   * WITHOUT throwing the draft away. Distinct from `clear()` on purpose: the
   * saved answers stay put so a mid-session refresh still protects them; the
   * applicant just never gets asked about them again.
   */
  const dismissRestore = useCallback(() => {
    try {
      window.localStorage.setItem(dismissKeyFor(storageKey), "true");
    } catch (e) {
      // Storage disabled — the banner still closes for this visit via the
      // caller's own state; it just can't be remembered.
    }
    setRestored(null);
  }, [storageKey]);

  return { restored, checked, save, clear, dismissRestore };
};

export default usePublicFormDraft;
